import { useState } from "react";
import { userService } from "../../services/userService";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useToast } from "../ui/ToastContainer";
import {
  parseApiError,
  formatValidationErrors,
} from "../../utils/errorHandler";
import { PermissionsSelector } from "./PermissionsSelector";
import { Permission } from "../../types";
import { PhoneInput, isValidE164Phone } from "../ui/PhoneInput";

interface AddUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  allowedRoles?: string[]; // Optional: limit roles that can be created
  defaultRole?: string;
  title?: string;
  canCreateAdmin?: boolean; // Allow creating admin users
}

export function AddUserDialog({
  isOpen,
  onClose,
  onSuccess,
  allowedRoles = ["student", "instructor", "admin"],
  defaultRole = "student",
  title = "Add New User",
  canCreateAdmin = false,
}: AddUserDialogProps) {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    role: defaultRole,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>(
    [],
  );
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Phone is optional, but if a value was typed it must be a complete
    // E.164 number (server is authoritative and re-validates too).
    if (phoneNumber && !isValidE164Phone(phoneNumber)) {
      setPhoneError("Please enter a complete phone number");
      return;
    }
    setPhoneError(null);

    setIsLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      const userData: any = {
        ...formData,
        skipEmailVerification: true,
      };

      // Only include phoneNumber when a value was provided. The backend
      // treats it as optional and ignores empty strings.
      if (phoneNumber) {
        userData.phoneNumber = phoneNumber;
      }

      if (formData.role === "admin" && canCreateAdmin) {
        userData.permissions = isSuperAdmin ? [] : selectedPermissions;
        userData.isSuperAdmin = isSuperAdmin;
      }

      await userService.createUser(userData);
      showSuccess("User created successfully", "Success");
      onSuccess();
      onClose();
      // Reset form
      setFormData({
        username: "",
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        role: defaultRole,
      });
      setSelectedPermissions([]);
      setIsSuperAdmin(false);
      setPhoneNumber("");
      setPhoneError(null);
    } catch (err: any) {
      console.error("Create user error:", err);
      const apiError = parseApiError(err);

      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix the validation errors below.");
      } else {
        setError(apiError.message);
      }

      showError(apiError.message, "Create Failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            <p className="font-medium">{error}</p>
            {Object.keys(validationErrors).length > 0 && (
              <ul className="mt-2 ml-4 list-disc space-y-1 text-xs">
                {formatValidationErrors(validationErrors).map((err, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{err.field}:</span>{" "}
                    {err.messages.join(", ")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label
              htmlFor="firstName"
              className="text-sm font-medium text-gray-700"
            >
              First Name
            </label>
            <Input
              id="firstName"
              placeholder="First name"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="lastName"
              className="text-sm font-medium text-gray-700"
            >
              Last Name
            </label>
            <Input
              id="lastName"
              placeholder="Last name"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="username"
            className="text-sm font-medium text-gray-700"
          >
            Username
          </label>
          <Input
            id="username"
            placeholder="Choose a username"
            value={formData.username}
            onChange={handleChange}
            required
            autoComplete="username"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email Address
          </label>
          <Input
            id="email"
            type="email"
            placeholder="user@example.com"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="email"
          />
        </div>

        <div className="space-y-2">
          <PhoneInput
            value={phoneNumber}
            onChange={(e164) => {
              setPhoneNumber(e164);
              if (phoneError) setPhoneError(null);
            }}
            label="Phone Number"
            placeholder="50 123 4567"
            optional
            error={phoneError}
            helperText="Include country code (e.g., +971 50 123 4567)"
          />
        </div>

        {/* Role Selection */}
        {allowedRoles.length > 1 && (
          <div className="space-y-2">
            <label htmlFor="role" className="text-sm font-medium text-gray-700">
              Role
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors"
            >
              {allowedRoles.map((role) => (
                <option key={role} value={role} className="capitalize">
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Admin-specific fields */}
        {formData.role === "admin" && canCreateAdmin && (
          <>
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="isSuperAdmin"
                checked={isSuperAdmin}
                onChange={(e) => {
                  setIsSuperAdmin(e.target.checked);
                  if (e.target.checked) {
                    setSelectedPermissions([]);
                  }
                }}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <label
                  htmlFor="isSuperAdmin"
                  className="text-sm font-medium text-gray-900 cursor-pointer"
                >
                  Super Admin
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Super admins have all permissions and can manage other admins.
                </p>
              </div>
            </div>

            {!isSuperAdmin && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Permissions
                </label>
                <div className="max-h-64 overflow-y-auto">
                  <PermissionsSelector
                    selectedPermissions={selectedPermissions}
                    onChange={setSelectedPermissions}
                    compact
                  />
                </div>
              </div>
            )}
          </>
        )}

        <div className="space-y-2">
          <label
            htmlFor="password"
            className="text-sm font-medium text-gray-700"
          >
            Password
          </label>
          <Input
            id="password"
            type="password"
            placeholder="Enter password"
            value={formData.password}
            onChange={handleChange}
            required
            autoComplete="new-password"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            className="w-32"
          >
            {isLoading ? "Creating..." : "Create User"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
